import { FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';

import { mensagemDeErro } from '@/lib/erros';
import { Dropzone } from '@/ui/components/dropzone';
import { toast } from '@/ui/primitives/aviso';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { type ResultadoImportacaoCentrosCusto } from '@fiscoai/contracts';

import { baixarPlanilhaCentrosCusto, useImportarCentrosCusto } from './api';
import { ACAO_IMPORTACAO } from './rotulos';

interface Props {
  empresaId: string;
  aberto: boolean;
  aoFechar: () => void;
}

function Totais({ r }: { r: ResultadoImportacaoCentrosCusto }) {
  const itens = [
    ['CRIAR', r.totais.criar],
    ['ATUALIZAR', r.totais.atualizar],
    ['SEM_MUDANCA', r.totais.semMudanca],
    ['ERRO', r.totais.erros],
  ] as const;
  return (
    <div className="flex flex-wrap gap-1.5" aria-label="Resumo da planilha">
      {itens
        .filter(([, n]) => n > 0)
        .map(([acao, n]) => (
          <Badge key={acao} tom={ACAO_IMPORTACAO[acao].tom}>
            <span className="tabular">{n}</span> {ACAO_IMPORTACAO[acao].total(n)}
          </Badge>
        ))}
    </div>
  );
}

/** Linhas que mudam algo ou têm erro (as sem mudança só contam no resumo). */
function Previa({ r }: { r: ResultadoImportacaoCentrosCusto }) {
  const relevantes = r.linhas.filter((l) => l.acao !== 'SEM_MUDANCA');
  if (relevantes.length === 0) return null;
  return (
    <ol className="max-h-72 divide-y divide-borda overflow-y-auto rounded-cartao border border-borda text-sm">
      {relevantes.map((l) => (
        <li key={l.linha} className="flex items-start gap-3 px-3 py-2">
          <span className="w-12 shrink-0 tabular text-texto-fraco">L{l.linha}</span>
          <span className="w-28 shrink-0 truncate font-mono">{l.codigo || '—'}</span>
          <span className="grid min-w-0 flex-1 gap-0.5">
            <Badge tom={ACAO_IMPORTACAO[l.acao].tom} className="justify-self-start">
              {ACAO_IMPORTACAO[l.acao].rotulo}
            </Badge>
            {l.erros.map((e) => (
              <span key={e} className="text-xs text-perigo">
                {e}
              </span>
            ))}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Importa o cadastro por planilha CSV (`codigo;descricao;natureza;ativo`): primeiro mostra o que
 * vai mudar, linha a linha; grava só se não houver erro (tudo ou nada). Nunca exclui.
 */
export function DialogoImportarCentrosCusto({ empresaId, aberto, aoFechar }: Props) {
  const importar = useImportarCentrosCusto(empresaId);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<ResultadoImportacaoCentrosCusto | null>(null);
  const fechar = () => {
    setArquivo(null);
    setPrevia(null);
    importar.reset();
    aoFechar();
  };
  const enviar = async (escolhido: File, confirmar: boolean) => {
    try {
      const r = await importar.mutateAsync({ arquivo: escolhido, confirmar });
      if (!r.confirmado) {
        setPrevia(r);
        return;
      }
      toast.success('Centros de custo importados');
      fechar();
    } catch (erro) {
      toast.error(mensagemDeErro(erro));
    }
  };
  const podeConfirmar =
    arquivo !== null &&
    previa !== null &&
    previa.totais.erros === 0 &&
    previa.totais.criar + previa.totais.atualizar > 0;

  return (
    <Dialog
      aberto={aberto}
      aoMudar={(abrir) => {
        if (!abrir) fechar();
      }}
      titulo="Importar centros de custo"
      descricao={
        <>
          CSV com <span className="font-mono">codigo;descricao;natureza;ativo</span>. Códigos que já
          existem são atualizados; nada é excluído.
        </>
      }
      className="w-dialogo-largo"
      acoes={
        <>
          <Button variante="fantasma" onClick={fechar}>
            Cancelar
          </Button>
          <Button
            variante="primario"
            disabled={!podeConfirmar}
            carregando={importar.isPending && previa !== null}
            onClick={() => {
              if (arquivo) void enviar(arquivo, true);
            }}
          >
            Importar
          </Button>
        </>
      }
    >
      <Dropzone
        aceita={['.csv', '.txt']}
        multiplo={false}
        titulo={arquivo ? arquivo.name : 'Solte a planilha CSV aqui'}
        descricao={arquivo ? 'Escolha outro arquivo para trocar' : 'ou clique para escolher'}
        disabled={importar.isPending}
        aoSoltar={([escolhido]) => {
          if (!escolhido) return;
          setArquivo(escolhido);
          setPrevia(null);
          void enviar(escolhido, false);
        }}
      />
      <p className="-mt-2 text-xs text-texto-fraco">
        Precisa de um modelo?{' '}
        <Button
          variante="link"
          className="text-xs"
          onClick={() => void baixarPlanilhaCentrosCusto(empresaId)}
        >
          Baixar o cadastro atual
        </Button>
      </p>
      {previa && (
        <div className="grid gap-3">
          <Totais r={previa} />
          {previa.totais.erros > 0 && (
            <p className="flex items-center gap-1.5 text-sm text-perigo">
              <FileSpreadsheet className="size-4" aria-hidden />
              Corrija as linhas com erro e envie de novo — nada foi gravado.
            </p>
          )}
          <Previa r={previa} />
        </div>
      )}
    </Dialog>
  );
}
